import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackFormSubmission, trackConversion } from '../utils/analytics';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });
  
  const [attachedFile, setAttachedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      // Check file type (PDF, images, excel)
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload PDF, JPG, PNG, or Excel files only');
        return;
      }
      
      setAttachedFile(file);
      toast.success('File attached successfully');
    }
  };
  
  const removeFile = () => {
    setAttachedFile(null);
    // Reset file input
    const fileInput = document.getElementById('portfolio-file');
    if (fileInput) fileInput.value = '';
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.phone || !formData.city || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check word limit (minimum 1 word, maximum 100 words)
    const wordCount = formData.message.trim().split(/\s+/).filter(word => word.length > 0).length;
    if (wordCount < 1) {
      toast.error('Please enter at least 1 word');
      return;
    }
    if (wordCount > 100) {
      toast.error('Service description must be 100 words or less');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('full_name', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('city', formData.city);
      submitData.append('message', formData.message);
      submitData.append('inquiry_type', 'general');
      
      if (attachedFile) {
        submitData.append('portfolio_file', attachedFile);
      }
      
      await axios.post(`${API}/contact`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Track successful form submission in Google Analytics
      trackFormSubmission('contact', {
        city: formData.city,
        inquiry_type: 'general',
        has_attachment: !!attachedFile
      });
      
      // Track conversion
      trackConversion('contact_form_submitted', {
        form_location: 'contact_page',
        has_portfolio_attachment: !!attachedFile
      });
      
      setIsSubmitted(true);
      toast.success('Thank you! Your inquiry has been submitted successfully.');
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        city: '',
        message: ''
      });
      setAttachedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('portfolio-file');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  
  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 70440 55500', '033-35062626'],
      action: 'Call us for immediate assistance'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['rumtekmf@gmail.com'],
      action: 'Send us your queries'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: ['15 S. P. Mukherjee Road, 3rd Floor', 'Kolkata 700025, West Bengal, India', '(Opposite Purna Cinema, above IDBI Bank)'],
      action: 'Visit us for personalized consultation'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Sat: 9:00 AM - 1:00 PM'],
      action: 'We are available during market hours'
    }
  ];
  
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center" data-testid="success-message">
          <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your inquiry has been submitted successfully. Our team will contact you within 24 hours.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            data-testid="submit-another-inquiry-btn"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Submit Another Inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...pageSEO.contact} faqJsonLd={faqSchemas.contact} />
      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6" data-testid="contact-title">
            Get in Touch
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-testid="contact-description">
            Ready to start your investment journey? We're here to help you achieve your financial goals. 
            Contact our expert team for personalized investment advice.
          </p>
        </div>
      </section>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="contact-form-title">
                Send Us Your Inquiry
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your full name"
                    data-testid="full-name-input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+91 9999999999"
                    data-testid="phone-input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your.email@example.com"
                    data-testid="email-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your city"
                    data-testid="city-input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Required (1-100 words) *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    maxLength="600"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Please describe the service you require in detail (1-100 words)..."
                    data-testid="message-input"
                    required
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {formData.message.length}/600 characters ({Math.ceil(formData.message.split(' ').filter(word => word.length > 0).length)} words)
                  </div>
                </div>
                
                {/* File Upload Section */}
                <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    📊 Attach Portfolio Statement (Optional - for FREE Portfolio Audit)
                  </label>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload your DP Holding Statement or Mutual Fund CAS for a complimentary portfolio review
                  </p>
                  
                  {!attachedFile ? (
                    <div className="relative">
                      <input
                        type="file"
                        id="portfolio-file"
                        accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="portfolio-file"
                        className="flex flex-col items-center justify-center w-full p-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                      >
                        <Upload className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 font-medium">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG, or Excel (Max 10MB)</p>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-white border border-green-300 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Upload className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">{attachedFile.name}</p>
                          <p className="text-xs text-gray-500">{(attachedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="flex-shrink-0 ml-4 text-red-600 hover:text-red-800"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-3">
                    🔒 <strong>Privacy Assured:</strong> Your documents are handled confidentially and used only for portfolio analysis.
                  </p>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-inquiry-btn"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Inquiry
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="space-y-8">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6" data-testid={`contact-info-${index}`}>
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                      <div className="space-y-1 mb-3">
                        {info.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="text-gray-600">{detail}</div>
                        ))}
                      </div>
                      <p className="text-sm text-blue-600">{info.action}</p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white" data-testid="quick-stats">
              <h3 className="text-lg font-semibold mb-4">Why Choose Rumtek?</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-blue-100">Experience</span>
                  <span className="font-semibold">25+ Years</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-100">Active Clients</span>
                  <span className="font-semibold">5000+</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-100">Assets Under Management</span>
                  <span className="font-semibold">₹300+ Cr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-100">Response Time</span>
                  <span className="font-semibold">&lt; 24 Hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-testid="faq-title">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about our services and investment process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="faq-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What is the minimum investment amount?
              </h3>
              <p className="text-gray-600">
                Minimum investment varies by instrument: ₹500 for SIP mutual funds, ₹10,000 for direct equity, 
                and ₹50 lakhs for PMS. We help you start with what suits your budget.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="faq-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Do you provide personalized investment advice?
              </h3>
              <p className="text-gray-600">
                Yes, we provide completely personalized investment advice based on your financial goals, 
                risk tolerance, and investment timeline. Each client gets a customized portfolio.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="faq-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How often do you review portfolios?
              </h3>
              <p className="text-gray-600">
                We conduct quarterly portfolio reviews for all clients and provide detailed performance reports. 
                For PMS clients, reviews are monthly with real-time monitoring.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="faq-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What are your fees and charges?
              </h3>
              <p className="text-gray-600">
                Our fee structure is transparent and competitive. For mutual funds, we work on trail commissions. 
                For PMS and advisory, we charge a percentage of assets under management.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;